import {
  RenterAssessmentParams,
  RenterProfileAssessment,
  RenterAssessmentItem,
  RenterAccessmentBadge,
} from '../../interfaces/renter-profile-assessment.interface';
import { getCreditScoreAssessment, creditScoreAssessmentBadges } from './credit-score-assessment.util';
import { getHousingVoucherAssessment } from './housing-voucher-assessment.util';
import { getIdentityVerificationAssessment } from './identity-verification-assessment.util';
import { getIncomeAssessment, incomeAssessmentBadges } from './income-assessment.util';
import { getMoveInDateAssessment } from './move-in-date-assessment.util';
import { getPetsAssessment } from './pets-assessment.util';

/**
 * ISSUE: currently badges do not support cases when renter has a co applicant with
 * sufficient credit score or income. As a temporary workaround, we disable red badges
 *
 * TODO: Remove this configuration once red badges for credit and income are properly reworked
 */
const disabledBadges = {
  creditScore: [creditScoreAssessmentBadges.low, creditScoreAssessmentBadges.noHistory],
  income: [incomeAssessmentBadges.low],
} as const;

class RenterProfileAssessmentService {
  /**
   * Checks if a badge should be excluded from the final badge list
   * @param badge - The badge to check
   * @returns true if the badge should be filtered out
   */
  private shouldExcludeBadge(badge: RenterAccessmentBadge): boolean {
    const allDisabledBadges = [...disabledBadges.creditScore, ...disabledBadges.income];
    return allDisabledBadges.some(
      (disabledBadge) => badge.title === disabledBadge.title && badge.badgeColor === disabledBadge.badgeColor,
    );
  }

  assessRenterProfile(assessmentParams: RenterAssessmentParams): RenterProfileAssessment | null {
    if (!assessmentParams.renter || !assessmentParams.property) {
      return null;
    }

    const creditScore = getCreditScoreAssessment(assessmentParams);
    const income = getIncomeAssessment(assessmentParams);
    const moveInDate = getMoveInDateAssessment(assessmentParams);
    const pets = getPetsAssessment(assessmentParams);
    const housingVoucher = getHousingVoucherAssessment(assessmentParams);
    const identityVerification = getIdentityVerificationAssessment(assessmentParams);

    const processedBadgesList = this.buildProcessedBadgesList({
      creditScore,
      income,
      moveInDate,
      pets,
      housingVoucher,
      identityVerification,
    });

    return {
      creditScore,
      income,
      moveInDate,
      pets,
      housingVoucher,
      identityVerification,
      processedBadgesList,
    };
  }

  private buildProcessedBadgesList(assessments: {
    creditScore: RenterAssessmentItem;
    income: RenterAssessmentItem;
    moveInDate: RenterAssessmentItem;
    pets: RenterAssessmentItem;
    housingVoucher: RenterAssessmentItem;
    identityVerification: RenterAssessmentItem;
  }): RenterAccessmentBadge[] {
    const badges: RenterAccessmentBadge[] = [];

    const allBadges = [
      assessments.creditScore.badge,
      assessments.income.badge,
      assessments.moveInDate.badge,
      assessments.pets.badge,
      assessments.housingVoucher.badge,
      assessments.identityVerification.badge,
    ].filter((badge): badge is RenterAccessmentBadge => badge !== null);

    // Filter out temporarily disabled badges (credit score and income red badges)
    const enabledBadges = allBadges.filter((badge) => !this.shouldExcludeBadge(badge));

    const creditBadge = assessments.creditScore.badge;
    const incomeBadge = assessments.income.badge;

    const hasCreditPass =
      creditBadge?.title === creditScoreAssessmentBadges.pass.title &&
      creditBadge?.badgeColor === creditScoreAssessmentBadges.pass.badgeColor;
    const hasIncomePass =
      incomeBadge?.title === incomeAssessmentBadges.pass.title &&
      incomeBadge?.badgeColor === incomeAssessmentBadges.pass.badgeColor;

    const hasCreditBelow =
      creditBadge?.title === creditScoreAssessmentBadges.low.title &&
      creditBadge?.badgeColor === creditScoreAssessmentBadges.low.badgeColor;
    const hasIncomeBelow =
      incomeBadge?.title === incomeAssessmentBadges.low.title &&
      incomeBadge?.badgeColor === incomeAssessmentBadges.low.badgeColor;

    if (hasCreditPass && hasIncomePass) {
      // Replace both with combined green badge
      badges.push({ title: 'Meets credit & income requirements', badgeColor: 'green' });
      badges.push(
        ...enabledBadges.filter(
          (badge) =>
            badge.title !== creditScoreAssessmentBadges.pass.title && badge.title !== incomeAssessmentBadges.pass.title,
        ),
      );
    } else if (hasCreditBelow && hasIncomeBelow) {
      // Replace both with combined red badge
      badges.push({ title: 'Credit and income requirements not met', badgeColor: 'red' });
      badges.push(
        ...enabledBadges.filter(
          (badge) =>
            badge.title !== creditScoreAssessmentBadges.low.title && badge.title !== incomeAssessmentBadges.low.title,
        ),
      );
    } else {
      // Use all badges as-is
      badges.push(...enabledBadges);
    }

    // Sort badges: green first, then neutral, then in-flight, red last
    return badges.sort((a, b) => {
      const colorOrder = { green: 1, grey: 2, yellow: 3, red: 4 };
      const unranked = Object.keys(colorOrder).length + 1;
      const orderA = colorOrder[a.badgeColor as keyof typeof colorOrder] || unranked;
      const orderB = colorOrder[b.badgeColor as keyof typeof colorOrder] || unranked;
      return orderA - orderB;
    });
  }
}

export default new RenterProfileAssessmentService();
